import React from 'react';
import { Page } from '/imports/ui/Page';
import { PageBody } from '/imports/ui/PageBody';
import { PageHeading } from '/imports/ui/PageHeading';
import { Panel } from '/imports/ui/Panel';
import { Badge } from '/imports/ui/Badge';
import { NotPermittedPage } from '/imports/ui/NotPermittedPage';
import { useUser } from '/imports/hooks';

/**
 * What the numbers mean, and where they lie.
 *
 * Deliberately shorter than the mogbots-ops reference's equivalent. Most of the
 * per-metric caveats already sit next to the metric they distort -- the warning
 * panels on Performance and Fleet P&L -- which is where a reader actually needs
 * them. This page covers only the things that span screens: the definitions a
 * reader has to hold in their head, and the data limits behind every figure.
 */

const Term = ({ term, children }: { term: string; children: React.ReactNode }) => (
  <div className="grid gap-1 border-b border-soft py-2.5 last:border-0 sm:grid-cols-[180px_1fr] sm:gap-4">
    <div className="font-semibold text-[13px] text-fg-1">{term}</div>
    <div className="text-[13px] leading-relaxed text-fg-2">{children}</div>
  </div>
);

export const AnalyticsNotesPage = () => {
  const user = useUser();
  if (!user?.isSuperUser) return <NotPermittedPage />;

  return (
    <Page>
      <PageHeading left={<h1>Analytics notes</h1>} />
      <PageBody>
        <div className="flex max-w-[820px] flex-col gap-3 pb-6">
          <Panel eyebrow="Start here" title="What these numbers are built from">
            <div className="flex flex-col gap-2 text-[13px] leading-relaxed text-fg-2">
              <p>
                Four collections carry everything. They differ enormously in how complete they are,
                and most surprises on these screens trace back to which one a figure came from.
              </p>
              <div className="mt-1 flex flex-col">
                <Term term="escrows">
                  One row per trade, back to <strong>2018</strong>. Deep and reliable, but records only
                  that a trade happened and whether it completed. Everything on Traders comes from here.
                </Term>
                <Term term="tradeItems">
                  The line items of completed trades. Currently <strong>buy-side only</strong> and it
                  stops in May, so anything involving a sale reads as zero rather than as unknown.
                </Term>
                <Term term="priceHistory">
                  Our prices and goatbots&apos; side by side, refreshed by the scrape. The most current
                  data in the system, and the only source that is genuinely up to date.
                </Term>
                <Term term="tradeSessions">
                  Per-trade detail: binder size, what we wanted, how far it got. Added recently, so it
                  holds nothing until the fleet trades again.
                </Term>
              </div>
            </div>
          </Panel>

          <Panel eyebrow="Money" title="How profit is calculated">
            <div className="flex flex-col">
              <Term term="Realised margin">
                Only exists when a card we bought is later sold. Buying is acquiring inventory and
                books no profit on its own, even inside a trade where we also sell.
              </Term>
              <Term term="LIFO cost basis">
                When several copies were bought at different prices, a sale is charged against the
                <strong> most recently bought</strong> copy. This tracks replacement cost, which suits a
                fleet that rebuys constantly at moving prices. The basis is a parameter in the code, so
                switching to FIFO is a one-line change &mdash; and it materially moves the number: on a
                rising market FIFO books more profit than LIFO on identical trades.
              </Term>
              <Term term="Net flow">
                Tickets in minus tickets out. Needs no cost model at all, so it is correct even where
                margin is not. Note it is a <strong>cash flow, not a profit</strong>: a good buying week
                makes it negative, because the stock acquired is not counted.
              </Term>
              <Term term="Underwater">
                Held stock whose average purchase price is above what we currently ask. Distinct from
                dead stock: an underwater card may be turning over briskly and still lose on every copy.
              </Term>
            </div>
          </Panel>

          <Panel eyebrow="Trades" title="Vocabulary">
            <div className="flex flex-col">
              <Term term="Customer vs internal">
                Bots trade with each other constantly &mdash; tickets from a banker, cards from a depot.
                Those are excluded everywhere, because they always complete and would flatter every
                conversion rate. Where they cannot be excluded, the screen says so.
              </Term>
              <Term term="Opened">
                Means <strong>we accepted</strong>, not that an invitation arrived. Invitations declined
                for blacklist, census or busy never produce a record.
              </Term>
              <Term term="Outcomes">
                <span className="mr-1"><Badge tone="neutral">cancelled</Badge></span> died before the bot
                looked at the binder ·
                <span className="mx-1"><Badge tone="amber">no offer</Badge></span> looked and wanted
                nothing ·
                <span className="mx-1"><Badge tone="amber">no buy</Badge></span> we offered, they did not
                engage ·
                <span className="mx-1"><Badge tone="gold">no sale</Badge></span> a deal existed but never
                closed ·
                <span className="mx-1"><Badge tone="green">completed</Badge></span>.
                The first two are a targeting story, the rest a customer one.
              </Term>
              <Term term="New vs returning">
                Measured against <strong>all history</strong>, never the selected range. Someone who last
                traded in 2019 and came back today is returning. Scoping it to the window would relabel
                every dormant trader as a fresh acquisition whenever the range moved.
              </Term>
            </div>
          </Panel>

          <Panel eyebrow="Caveats" title="Where the numbers are weaker than they look">
            <div className="flex flex-col">
              <Term term="Depot transfers">
                Cards move between bots via a depot with no ledger line, so a bot can sell stock it has
                no recorded purchase for. Per-bot <strong>margin</strong> is overstated for those bots;
                per-bot net flow is unaffected. Fleet totals are fine, since the stock never left.
              </Term>
              <Term term="The pricing floor">
                Prices bottom out at 0.001 and the sell floor is 0.008, so on bulk the sell/buy ratio is
                a rounding artifact &mdash; the median spread reads 6.0x with no floor and 1.59x on cards
                worth a ticket. Any spread quoted without a floor is meaningless.
              </Term>
              <Term term="Idle vs never sold">
                &ldquo;No record&rdquo; on dead stock does not mean a card never traded: depot transfers
                update holdings without writing a ledger line. <strong>Never sold</strong> is the
                trustworthy signal, because a sale to a customer is always recorded.
              </Term>
              <Term term="Binder contents">
                Trade sessions store the binder <strong>count</strong>, not the cards. Storing every
                binder would be the largest write in the system, so the inspector shows the shape of a
                trade and the cards we wanted, but cannot replay their full binder.
              </Term>
              <Term term="Cash bots">
                Cash mode prices in dollars while everything else prices in tickets, and both land in the
                same column. USD lines are excluded from margin rather than converted; the count of what
                was dropped is shown where it matters.
              </Term>
            </div>
          </Panel>
        </div>
      </PageBody>
    </Page>
  );
};
